import {
  Component,
  HostListener,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { LayoutService } from '../layout/layout.service';
import {
  GlobalSearchDialogComponent,
  GlobalSearchDialogData,
} from './global-search-dialog.component';

const MOBILE_BREAKPOINT = '(max-width: 599px)';

/**
 * Trigger della command palette montato nella toolbar.
 * - Desktop: campo compatto "Cerca…" con scorciatoia ⌘K / Ctrl+K, aperto come dialog classico.
 * - Mobile: solo icona lente, aperto come bottom sheet a schermo intero.
 * - Ctrl/Cmd+K apre (o richiude) l'overlay ovunque nell'app.
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnDestroy {
  private dialog = inject(MatDialog);
  private bottomSheet = inject(MatBottomSheet);
  private layoutService = inject(LayoutService);
  private router = inject(Router);

  private dialogRef?: MatDialogRef<GlobalSearchDialogComponent>;
  private bottomSheetRef?: MatBottomSheetRef<GlobalSearchDialogComponent>;

  /** Etichetta scorciatoia in base alla piattaforma. */
  readonly shortcutLabel = this.isMac() ? '⌘K' : 'Ctrl K';

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.toggle();
    }
  }

  toggle(): void {
    if (this.dialogRef || this.bottomSheetRef) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (this.dialogRef || this.bottomSheetRef) {
      return; // evita doppie aperture
    }

    const data: GlobalSearchDialogData = {
      organizationId: this.resolveOrganizationId(),
    };

    if (this.isMobileViewport()) {
      this.bottomSheetRef = this.bottomSheet.open(GlobalSearchDialogComponent, {
        panelClass: 'global-search-bottom-sheet',
        autoFocus: 'first-tabbable',
        data,
      });

      this.bottomSheetRef.afterDismissed().subscribe(() => {
        this.bottomSheetRef = undefined;
      });
      return;
    }

    this.dialogRef = this.dialog.open(GlobalSearchDialogComponent, {
      panelClass: 'global-search-dialog',
      width: '640px',
      maxWidth: '90vw',
      position: { top: '10vh' },
      autoFocus: 'first-tabbable',
      data,
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  private close(): void {
    this.dialogRef?.close();
    this.bottomSheetRef?.dismiss();
  }

  private isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia(MOBILE_BREAKPOINT).matches
    );
  }

  private resolveOrganizationId(): number | null {
    const fromLayout = Number(this.layoutService.organizationId$.value);
    if (fromLayout && !isNaN(fromLayout)) {
      return fromLayout;
    }
    // Fallback: primo segmento numerico dell'URL (es. /12/dashboard).
    const match = this.router.url.split('?')[0].match(/^\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  private isMac(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent)
    );
  }

  ngOnDestroy(): void {
    this.close();
  }
}
